import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Flashcard, FlashcardsState } from '../../types/Flashcard';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { cacheContentImages } from '../../utils/imgCaching';
import moment from 'moment';

const initialState: FlashcardsState = {
  new: [],
  due: [],
  completed: [],
};

const cacheFirstTwoFlashcards = (state: FlashcardsState) => {
  state.new.slice(0, 2).forEach((flashcard) => {
    cacheContentImages(flashcard);
  });
  state.due.slice(0, 2).forEach((flashcard) => {
    cacheContentImages(flashcard);
  });
};

export const flashcardsSlice = createSlice({
  name: 'flashcards',
  initialState,
  reducers: {
    flashcardsLoaded: (state, action: PayloadAction<FlashcardsState>) => {
      state.new = action.payload.new;
      state.due = action.payload.due;
      state.completed = action.payload.completed;

      state.due.sort((a, b) => {
        const dateA = new Date(a.scheduling.due);
        const dateB = new Date(b.scheduling.due);
        return dateA.getTime() - dateB.getTime();
      });

      // Cache images for first two flashcards
      cacheFirstTwoFlashcards(state);
    },
    flashcardUpdated: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = action.payload;

      // Remove from all arrays
      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);
      state.completed = state.completed.filter((f) => f.id !== flashcard.id);

      const startOfTomorrow = moment().add(1, 'day').startOf('day');

      // Add to appropriate array
      if (
        flashcard.scheduling.state === 0 &&
        !flashcard.scheduling.isSuspended
      ) {
        state.new.push(flashcard);
      } else if (
        flashcard.scheduling.state !== 0 &&
        !flashcard.scheduling.isSuspended &&
        moment(flashcard.scheduling.due).isBefore(startOfTomorrow)
      ) {
        insertSorted(state.due, flashcard);
      } else {
        state.completed.push(flashcard);
      }

      // Cache images for first two flashcards after update
      cacheFirstTwoFlashcards(state);
    },
    flashcardSuspended: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = action.payload;
      const wasInNew = state.new.some((f) => f.id === flashcard.id);
      const wasInDue = state.due.some((f) => f.id === flashcard.id);

      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);

      if (wasInNew || wasInDue) {
        state.completed.push(flashcard);
        // Cache images since we removed a flashcard that might have been in first two
        cacheFirstTwoFlashcards(state);
      }
    },
    flashcardRemoved: (state, action: PayloadAction<Flashcard>) => {
      const flashcard = action.payload;
      state.new = state.new.filter((f) => f.id !== flashcard.id);
      state.due = state.due.filter((f) => f.id !== flashcard.id);
      state.completed = state.completed.filter((f) => f.id !== flashcard.id);

      // Cache images since we removed a flashcard that might have been in first two
      cacheFirstTwoFlashcards(state);
    },
    unshiftNew: (state, action: PayloadAction<Flashcard>) => {
      state.new.unshift(action.payload);

      // Cache images since we added to the beginning, changing first two
      cacheFirstTwoFlashcards(state);
    },
    unshiftDue: (state, action: PayloadAction<Flashcard>) => {
      state.due.unshift(action.payload);
      // Remove duplicates from due flashcards by ID
      state.due = state.due.filter(
        (dueFlashcard, index, self) =>
          index === self.findIndex((t) => t.id === dueFlashcard.id)
      );

      // Cache images since we added to the beginning, changing first two
      cacheFirstTwoFlashcards(state);
    },
    clearFlashcards: () => {
      return initialState;
    },
  },
});

const insertSorted = (array: Flashcard[], item: Flashcard) => {
  let low = 0;
  let high = array.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    if (new Date(array[mid].scheduling.due) < new Date(item.scheduling.due)) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  array.splice(low, 0, item);
};

export const selectNewFlashcards = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return flashcards.new;
  }
);

export const selectDueFlashcards = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return flashcards.due;
  }
);

export const selectCompletedFlashcards = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return flashcards.completed;
  }
);

export const selectCompletedFlashcardsCount = createSelector(
  [(state: RootState) => state.flashcards],
  (flashcards) => {
    return flashcards.completed?.length || 0;
  }
);

export const {
  flashcardsLoaded,
  flashcardUpdated,
  flashcardSuspended,
  flashcardRemoved,
  unshiftNew,
  unshiftDue,
  clearFlashcards,
} = flashcardsSlice.actions;

export default flashcardsSlice.reducer;
